/**
 * Mirrors api_v2/errors.py. The code is the ONLY thing the UI branches on — messages are
 * human-facing and may be reworded at any time.
 *
 * Kept in sync by tests/api-error-codes.spec.ts, which fetches /api/v2/meta/ and asserts every
 * server code is represented here. A code that exists on only one side is a bug waiting for a
 * user to find it.
 */
export const ErrorCode = {
  VALIDATION_FAILED: 'validation_failed',
  VALIDATION_INVALID_ORDERING: 'validation_invalid_ordering',
  VALIDATION_INVALID_FILTER: 'validation_invalid_filter',

  AUTH_REQUIRED: 'auth_required',
  AUTH_INVALID_CREDENTIALS: 'auth_invalid_credentials',
  AUTH_TOKEN_EXPIRED: 'auth_token_expired',
  AUTH_INVALID_TOKEN: 'auth_invalid_token',
  AUTH_OTP_INVALID: 'auth_otp_invalid',
  AUTH_OTP_EXHAUSTED: 'auth_otp_exhausted',
  AUTH_ACCOUNT_DISABLED: 'auth_account_disabled',
  AUTH_ACCOUNT_LOCKED: 'auth_account_locked',

  PERMISSION_NO_WORKSPACE: 'permission_no_workspace',
  BUSINESS_PASSWORD_ALREADY_SET: 'business_password_already_set',

  /** Affiliate Operations (Phase 03). Mirrors `affiliates/errors.py`. */
  PERMISSION_AFFILIATE_INACTIVE: 'permission_affiliate_inactive',
  BUSINESS_INVALID_AFFILIATE_TRANSITION: 'business_invalid_affiliate_transition',
  BUSINESS_AFFILIATE_ALREADY_EXISTS: 'business_affiliate_already_exists',
  BUSINESS_AFFILIATE_ATTRIBUTION_CONFLICT: 'business_affiliate_attribution_conflict',
  BUSINESS_AFFILIATE_CODE_UNAVAILABLE: 'business_affiliate_code_unavailable',

  /** Landlord & Agent core (Phase 04). Mirrors `property_domain/errors.py`. */
  BUSINESS_QUOTA_EXCEEDED: 'business_quota_exceeded',
  BUSINESS_SPACE_OCCUPIED: 'business_space_occupied',
  BUSINESS_SPACE_OCCUPIED_NOT_ADVERTISABLE: 'business_space_occupied_not_advertisable',
  /** scope-freeze.md D7: a property with sitting tenants cannot be deleted. */
  BUSINESS_PROPERTY_OCCUPIED: 'business_property_occupied',
  BUSINESS_SERVICE_CHARGE_ACTIVE_EXISTS: 'business_service_charge_active_exists',
  BUSINESS_ACCOUNT_HAS_PAYMENT_HISTORY: 'business_account_has_payment_history',
  VALIDATION_INVALID_IMAGE: 'validation_invalid_image',

  /**
   * Property onboarding (Phase 05). Mirrors `onboarding/errors.py`. The validation codes are
   * also the `code` on each entry of a validation report's `errors`, so the wizard branches on
   * one vocabulary whether the report arrived from `/validate/` or the failure arrived as a 400.
   */
  VALIDATION_BLUEPRINT_OVER_ALLOCATED: 'validation_blueprint_over_allocated',
  VALIDATION_BLUEPRINT_TOTAL_TOO_LARGE: 'validation_blueprint_total_too_large',
  VALIDATION_EXEMPT_ALLOCATION_PRICED: 'validation_exempt_allocation_priced',
  VALIDATION_SECTION_CAPACITY_EXCEEDED: 'validation_section_capacity_exceeded',
  VALIDATION_SECTION_DEPTH_EXCEEDED: 'validation_section_depth_exceeded',
  VALIDATION_CONFIGURATION_REF: 'validation_configuration_ref',
  VALIDATION_CONFIGURATION_INVALID: 'validation_configuration_invalid',
  BUSINESS_BLUEPRINT_NOT_EDITABLE: 'business_blueprint_not_editable',
  BUSINESS_SECTION_HAS_SPACES: 'business_section_has_spaces',
  BUSINESS_RUN_NOT_REVERTIBLE: 'business_run_not_revertible',
  BUSINESS_SPACE_IDENTIFIER_CONFLICT: 'business_space_identifier_conflict',
  CONFLICT_BLUEPRINT_ALREADY_APPLIED: 'conflict_blueprint_already_applied',

  /**
   * The onboarding engine v3 (2026-09-11): authored pricing rules, tiers, floor plans, output
   * invariants, versioned drafts and the reviewed-plan fingerprint. Mirrors `onboarding/errors.py`.
   */
  VALIDATION_SELECTOR_INVALID: 'validation_selector_invalid',
  VALIDATION_PRICING_EFFECT_INVALID: 'validation_pricing_effect_invalid',
  VALIDATION_PRICING_SELECTOR_UNKNOWN_REF: 'validation_pricing_selector_unknown_ref',
  VALIDATION_PRICING_BASE_AMBIGUOUS: 'validation_pricing_base_ambiguous',
  VALIDATION_PRICING_FINAL_AMBIGUOUS: 'validation_pricing_final_ambiguous',
  VALIDATION_PRICING_BELOW_GUARDRAIL: 'validation_pricing_below_guardrail',
  VALIDATION_PRICING_ABOVE_GUARDRAIL: 'validation_pricing_above_guardrail',
  VALIDATION_UNIT_RULE_TARGET_MISSING: 'validation_unit_rule_target_missing',
  VALIDATION_TIER_OVERLAP: 'validation_tier_overlap',
  VALIDATION_TIER_GAP: 'validation_tier_gap',
  VALIDATION_GENERATION_MISMATCH: 'validation_generation_mismatch',
  VALIDATION_IDENTIFIER_DUPLICATE: 'validation_identifier_duplicate',
  VALIDATION_GENERATION_LIMIT_EXCEEDED: 'validation_generation_limit_exceeded',
  VALIDATION_TOPOLOGY_BAND_OVERLAP: 'validation_topology_band_overlap',
  VALIDATION_TOPOLOGY_SLOT_DUPLICATE: 'validation_topology_slot_duplicate',
  VALIDATION_TOPOLOGY_SLOT_TAKEN: 'validation_topology_slot_taken',
  VALIDATION_TOPOLOGY_FLOOR_PLAN_EMPTY: 'validation_topology_floor_plan_empty',
  VALIDATION_TOPOLOGY_FLOOR_EMPTY: 'validation_topology_floor_empty',
  VALIDATION_TOPOLOGY_SLOT_UNKNOWN: 'validation_topology_slot_unknown',
  VALIDATION_UNKNOWN_FIELD: 'validation_unknown_field',
  CONFLICT_DRAFT_VERSION: 'conflict_draft_version',
  /** 2026-10-28: the draft has created its property; it is neither saved over nor committed again. */
  CONFLICT_DRAFT_COMMITTED: 'conflict_draft_committed',
  CONFLICT_CONFIGURATION_CHANGED: 'conflict_configuration_changed',

  /** Affiliate onboarding (Phase 06). Mirrors `affiliates/errors.py` and `identity/errors.py`. */
  BUSINESS_LANDLORD_EMAIL_EXISTS: 'business_landlord_email_exists',
  BUSINESS_INVITE_LIMIT_REACHED: 'business_invite_limit_reached',
  BUSINESS_LANDLORD_ALREADY_ACTIVATED: 'business_landlord_already_activated',
  BUSINESS_AFFILIATE_ALREADY_ACTIVATED: 'business_affiliate_already_activated',
  BUSINESS_EMAIL_ALREADY_REGISTERED: 'business_email_already_registered',
  /**
   * Affiliate Officers Supervisor (2026-10-11). A supervisor acted on themselves or on another
   * supervisor; an administrator tried to make an officer who is not active a supervisor.
   * Mirrors `affiliates/errors.py`.
   */
  BUSINESS_SUPERVISOR_TARGET_NOT_ALLOWED: 'business_supervisor_target_not_allowed',
  BUSINESS_SUPERVISOR_REQUIRES_ACTIVE_OFFICER: 'business_supervisor_requires_active_officer',

  /** Tenancy & rent lifecycle (Phase 07). Mirrors `leasing/errors.py`. */
  BUSINESS_SPACE_ALREADY_OCCUPIED: 'business_space_already_occupied',
  BUSINESS_PROPERTY_AT_CAPACITY: 'business_property_at_capacity',
  BUSINESS_TENANCY_CLOSED: 'business_tenancy_closed',
  BUSINESS_TENANCY_HAS_UNSETTLED_PAYMENT: 'business_tenancy_has_unsettled_payment',
  BUSINESS_TENANCY_RENT_EXEMPT: 'business_tenancy_rent_exempt',
  BUSINESS_SPACE_NOT_PRICED: 'business_space_not_priced',
  BUSINESS_PROPERTY_PAYMENT_NOT_CONFIGURED: 'business_property_payment_not_configured',
  BUSINESS_UPFRONT_SESSIONS_EXCEEDED: 'business_upfront_sessions_exceeded',
  BUSINESS_RENEWAL_ALREADY_PENDING: 'business_renewal_already_pending',
  PERMISSION_OFFLINE_RECORDING_DENIED: 'permission_offline_recording_denied',
  PERMISSION_TENANCY_CREATE_DENIED: 'permission_tenancy_create_denied',
  PERMISSION_MOVE_OUT_DENIED: 'permission_move_out_denied',
  VALIDATION_RECEIVED_CONFIRMATION_REQUIRED: 'validation_received_confirmation_required',
  VALIDATION_AMOUNT_CONFIRMATION_REQUIRED: 'validation_amount_confirmation_required',
  VALIDATION_INVALID_EVIDENCE: 'validation_invalid_evidence',

  /** Public marketplace (Phase 08). Mirrors `marketplace/errors.py`. */
  VALIDATION_INVALID_PRICE_RANGE: 'validation_invalid_price_range',
  BUSINESS_ENQUIRY_REJECTED: 'business_enquiry_rejected',

  /**
   * Rental checkout (Phase 09). Mirrors the Phase 09 block in `leasing/errors.py`.
   *
   * Every one of these has a designed state in `modules/marketplace/checkout` — several of
   * them are the whole reason a step exists. `BUSINESS_QUOTE_STALE` in particular must never
   * be retried silently: the visitor re-confirms a total they can see.
   */
  BUSINESS_SPACE_ON_HOLD: 'business_space_on_hold',
  BUSINESS_RESERVATION_EXPIRED: 'business_reservation_expired',
  BUSINESS_HOLD_ALREADY_EXTENDED: 'business_hold_already_extended',
  BUSINESS_QUOTE_STALE: 'business_quote_stale',
  BUSINESS_CHECKOUT_NOT_PAYABLE: 'business_checkout_not_payable',
  BUSINESS_PAYMENT_NOT_SETTLED: 'business_payment_not_settled',
  PERMISSION_CHECKOUT_TOKEN_INVALID: 'permission_checkout_token_invalid',
  VALIDATION_TERMS_NOT_ACCEPTED: 'validation_terms_not_accepted',
  VALIDATION_APPLICANT_REQUIRED: 'validation_applicant_required',

  /**
   * Affiliate commission (Phase 10). Mirrors `commissions/errors.py`.
   *
   * Every one of these is an administrator's error — an officer has no mutation endpoint in
   * this module at all, so the only code they can ever see here is `permission_denied` or
   * `not_found`. `VALIDATION_COMMISSION_TOTAL_MISMATCH` is the important one: it means the
   * ledger moved under the approval dialog, and the batch was refused rather than approved
   * against a number nobody looked at.
   */
  BUSINESS_COMMISSION_RULE_IN_USE: 'business_commission_rule_in_use',
  BUSINESS_COMMISSION_RULE_OVERLAPS: 'business_commission_rule_overlaps',
  BUSINESS_COMMISSION_ENTRY_NOT_PENDING: 'business_commission_entry_not_pending',
  BUSINESS_COMMISSION_ENTRY_ALREADY_REVERSED: 'business_commission_entry_already_reversed',
  BUSINESS_COMMISSION_ENTRY_NOT_APPROVED: 'business_commission_entry_not_approved',
  BUSINESS_COMMISSION_ENTRY_ALREADY_IN_PAYOUT: 'business_commission_entry_already_in_payout',
  BUSINESS_COMMISSION_PAYOUT_EMPTY: 'business_commission_payout_empty',
  BUSINESS_COMMISSION_PAYOUT_NOT_DRAFT: 'business_commission_payout_not_draft',
  BUSINESS_AFFILIATE_NOT_PAYABLE: 'business_affiliate_not_payable',
  VALIDATION_COMMISSION_TOTAL_MISMATCH: 'validation_commission_total_mismatch',
  VALIDATION_COMMISSION_BATCH_TOO_LARGE: 'validation_commission_batch_too_large',
  VALIDATION_COMMISSION_REASON_REQUIRED: 'validation_commission_reason_required',
  PERMISSION_COMMISSION_READ_ONLY: 'permission_commission_read_only',

  // Phase 11. Analytics refuses a window it cannot serve rather than trying: an unbounded
  // daily series is a denial-of-service, not a big chart.
  VALIDATION_RANGE_TOO_LARGE: 'validation_range_too_large',
  VALIDATION_INVALID_RANGE: 'validation_invalid_range',
  VALIDATION_INVALID_GRANULARITY: 'validation_invalid_granularity',
  VALIDATION_UNKNOWN_REPORT: 'validation_unknown_report',
  // The admin map's state view asks for one Nigerian state (`?region=NG-EN`).
  VALIDATION_INVALID_REGION: 'validation_invalid_region',
  BUSINESS_EXPORT_NOT_READY: 'business_export_not_ready',
  BUSINESS_EXPORT_EXPIRED: 'business_export_expired',

  PERMISSION_DENIED: 'permission_denied',
  NOT_FOUND: 'not_found',

  BUSINESS_RULE_VIOLATION: 'business_rule_violation',
  CONFLICT: 'conflict',
  CONFLICT_IDEMPOTENCY_KEY_REUSE: 'conflict_idempotency_key_reuse',

  METHOD_NOT_ALLOWED: 'method_not_allowed',
  UNSUPPORTED_MEDIA_TYPE: 'unsupported_media_type',
  RATE_LIMITED: 'rate_limited',
  /** A live stream cannot open just now (503, realtime.md §4.3); never shown — the page polls. */
  LIVE_UPDATES_UNAVAILABLE: 'live_updates_unavailable',
  SERVER_ERROR: 'server_error',
  API_ERROR: 'api_error',

  /** Client-side only: the request never reached the server. */
  NETWORK_ERROR: 'network_error',

  /** Administration & operations (Phase 12). Mirrors `operations/errors.py` and `leasing/errors.py`. */
  VALIDATION_REASON_REQUIRED: 'validation_reason_required',
  VALIDATION_BULK_CONFIRMATION_MISMATCH: 'validation_bulk_confirmation_mismatch',
  VALIDATION_BULK_TOO_LARGE: 'validation_bulk_too_large',
  VALIDATION_AUDIT_RANGE_TOO_LARGE: 'validation_audit_range_too_large',
  BUSINESS_SIGNAL_CLOSED: 'business_signal_closed',
  BUSINESS_SPACE_HAS_TENANCY: 'business_space_has_tenancy',
  BUSINESS_PAYMENT_NOT_REFUNDABLE: 'business_payment_not_refundable',
  BUSINESS_PAYMENT_NOT_VERIFIABLE: 'business_payment_not_verifiable',

  /** Rollout, migration and verification (Phase 14). Mirrors `rollout/errors.py`. */
  VALIDATION_UNKNOWN_STAGE: 'validation_unknown_stage',
  BUSINESS_STAGE_OUT_OF_ORDER: 'business_stage_out_of_order',
  BUSINESS_BACKFILL_DECISION_REQUIRED: 'business_backfill_decision_required',
  BUSINESS_PARITY_UNAVAILABLE: 'business_parity_unavailable',
  BUSINESS_ATTRIBUTION_ALREADY_REVIEWED: 'business_attribution_already_reviewed',

  /** Students' space requests (2026-10-09). Mirrors `space_requests/errors.py`. */
  BUSINESS_SPACE_REQUEST_NOT_PENDING: 'business_space_request_not_pending',
  BUSINESS_SPACE_REQUEST_NOT_OPEN: 'business_space_request_not_open',
  /** Students looking for a roommate (2026-10-22). Mirrors `roommates/errors.py`. */
  BUSINESS_ROOMMATE_REQUEST_NOT_OPEN: 'business_roommate_request_not_open',
  BUSINESS_ROOMMATE_MATCH_CLOSED: 'business_roommate_match_closed',
  // An officer handling a roommate request (2026-10-26), as with space requests.
  BUSINESS_ROOMMATE_REQUEST_ALREADY_HANDLED: 'business_roommate_request_already_handled',
  BUSINESS_ROOMMATE_REQUEST_NOT_HANDLER: 'business_roommate_request_not_handler',
  /** Affiliate institutions (2026-10-22). Mirrors `institutions/errors.py`. */
  BUSINESS_INSTITUTION_EXISTS: 'business_institution_exists',
  BUSINESS_INSTITUTION_MEMBER_EXISTS: 'business_institution_member_exists',
  BUSINESS_INSTITUTION_INVALID_TRANSITION: 'business_institution_invalid_transition',
  BUSINESS_INSTITUTION_MEMBER_ALREADY_ACTIVATED: 'business_institution_member_already_activated',
  // An officer handling a student (2026-10-11).
  BUSINESS_SPACE_REQUEST_ALREADY_HANDLED: 'business_space_request_already_handled',
  BUSINESS_SPACE_REQUEST_NOT_HANDLER: 'business_space_request_not_handler',
  // Viewings an officer runs for the students they handle (2026-10-12).
  BUSINESS_VIEWING_FULL: 'business_viewing_full',
  BUSINESS_VIEWING_NOT_SCHEDULED: 'business_viewing_not_scheduled',
  BUSINESS_VIEWING_NOT_PAST: 'business_viewing_not_past',
  // APMP on WhatsApp (2026-10-12). Mirrors `whatsapp/errors.py`.
  VALIDATION_INVALID_DOCUMENT: 'validation_invalid_document',
  VALIDATION_WEBHOOK_TOO_LARGE: 'validation_webhook_too_large',
  BUSINESS_WHATSAPP_NOT_CONFIGURED: 'business_whatsapp_not_configured',
  BUSINESS_WHATSAPP_WINDOW_CLOSED: 'business_whatsapp_window_closed',
  BUSINESS_WHATSAPP_OPTED_OUT: 'business_whatsapp_opted_out',
  BUSINESS_WHATSAPP_GRAPH_ERROR: 'business_whatsapp_graph_error',
  BUSINESS_BOT_UNAVAILABLE: 'business_bot_unavailable',
  // Support officers (2026-10-12). Mirrors `support/errors.py`.
  BUSINESS_SUPPORT_OFFICER_EXISTS: 'business_support_officer_exists',
  BUSINESS_SUPPORT_OFFICER_ALREADY_ACTIVATED: 'business_support_officer_already_activated',
  BUSINESS_SUPPORT_OFFICER_INVALID_TRANSITION: 'business_support_officer_invalid_transition',
  /** Property media (2026-10-27). Mirrors `marketplace/errors.py`. */
  BUSINESS_MEDIA_LIMIT_REACHED: 'business_media_limit_reached',
  VALIDATION_INVALID_VIDEO: 'validation_invalid_video',
  CONFLICT_UPLOAD_OFFSET: 'conflict_upload_offset',
  /** Accounts on WhatsApp (2026-10-27). Mirrors `identity/errors.py`. */
  BUSINESS_PHONE_ALREADY_REGISTERED: 'business_phone_already_registered',
  BUSINESS_PHONE_VERIFIED_ELSEWHERE: 'business_phone_verified_elsewhere',
  VALIDATION_NO_WHATSAPP_NUMBER: 'validation_no_whatsapp_number',
  BUSINESS_WHATSAPP_CODE_NOT_SENT: 'business_whatsapp_code_not_sent',
  /** A landlord's dashboard waits for a confirmed number or email (2026-10-27, D25). */
  AUTH_CONTACT_UNCONFIRMED: 'auth_contact_unconfirmed',
} as const

export type ErrorCodeValue = (typeof ErrorCode)[keyof typeof ErrorCode]

/** Codes the user can act on themselves; anything else is "contact support". */
export const RECOVERABLE_CODES: ReadonlySet<string> = new Set([
  ErrorCode.VALIDATION_FAILED,
  ErrorCode.VALIDATION_INVALID_IMAGE,
  ErrorCode.VALIDATION_BLUEPRINT_OVER_ALLOCATED,
  ErrorCode.VALIDATION_BLUEPRINT_TOTAL_TOO_LARGE,
  ErrorCode.VALIDATION_EXEMPT_ALLOCATION_PRICED,
  ErrorCode.VALIDATION_SECTION_CAPACITY_EXCEEDED,
  ErrorCode.VALIDATION_SECTION_DEPTH_EXCEEDED,
  ErrorCode.BUSINESS_SECTION_HAS_SPACES,
  ErrorCode.BUSINESS_QUOTA_EXCEEDED,
  ErrorCode.BUSINESS_LANDLORD_EMAIL_EXISTS,
  ErrorCode.BUSINESS_INVITE_LIMIT_REACHED,
  ErrorCode.BUSINESS_EMAIL_ALREADY_REGISTERED,
  // Phase 07: each of these tells the landlord what to change and try again.
  ErrorCode.BUSINESS_SPACE_ALREADY_OCCUPIED,
  ErrorCode.BUSINESS_TENANCY_HAS_UNSETTLED_PAYMENT,
  ErrorCode.BUSINESS_SPACE_NOT_PRICED,
  ErrorCode.BUSINESS_PROPERTY_PAYMENT_NOT_CONFIGURED,
  ErrorCode.BUSINESS_UPFRONT_SESSIONS_EXCEEDED,
  ErrorCode.BUSINESS_RENEWAL_ALREADY_PENDING,
  ErrorCode.VALIDATION_RECEIVED_CONFIRMATION_REQUIRED,
  ErrorCode.VALIDATION_AMOUNT_CONFIRMATION_REQUIRED,
  ErrorCode.VALIDATION_INVALID_EVIDENCE,
  // Phase 08: a visitor can widen the price range, or retry the enquiry with a real message.
  ErrorCode.VALIDATION_INVALID_PRICE_RANGE,
  ErrorCode.BUSINESS_ENQUIRY_REJECTED,
  // Phase 09. Each of these has a next action the visitor can take themselves: pick another
  // space, re-reserve, re-confirm the new total, fill in the details, try paying again.
  // `PERMISSION_CHECKOUT_TOKEN_INVALID` is recoverable too — it means "start again in this
  // tab", which is a real action, not a dead end.
  ErrorCode.BUSINESS_SPACE_ON_HOLD,
  ErrorCode.BUSINESS_RESERVATION_EXPIRED,
  ErrorCode.BUSINESS_HOLD_ALREADY_EXTENDED,
  ErrorCode.BUSINESS_QUOTE_STALE,
  ErrorCode.BUSINESS_CHECKOUT_NOT_PAYABLE,
  ErrorCode.PERMISSION_CHECKOUT_TOKEN_INVALID,
  ErrorCode.VALIDATION_TERMS_NOT_ACCEPTED,
  ErrorCode.VALIDATION_APPLICANT_REQUIRED,
  // Phase 10. An administrator can fix each of these themselves: reload the approval queue and
  // confirm the current total, select fewer entries, pick a later start date for the new rule
  // version, give the adjustment a reason, or reinstate the officer before paying them.
  ErrorCode.VALIDATION_COMMISSION_TOTAL_MISMATCH,
  ErrorCode.VALIDATION_COMMISSION_BATCH_TOO_LARGE,
  ErrorCode.VALIDATION_COMMISSION_REASON_REQUIRED,
  ErrorCode.BUSINESS_COMMISSION_RULE_OVERLAPS,
  ErrorCode.BUSINESS_COMMISSION_RULE_IN_USE,
  ErrorCode.BUSINESS_COMMISSION_ENTRY_NOT_PENDING,
  ErrorCode.BUSINESS_COMMISSION_ENTRY_ALREADY_REVERSED,
  ErrorCode.BUSINESS_COMMISSION_ENTRY_ALREADY_IN_PAYOUT,
  ErrorCode.BUSINESS_COMMISSION_PAYOUT_EMPTY,
  ErrorCode.BUSINESS_COMMISSION_PAYOUT_NOT_DRAFT,
  ErrorCode.BUSINESS_AFFILIATE_NOT_PAYABLE,
  // Phase 11. Every one of these has an action the viewer can take right there: narrow the
  // range, pick a coarser granularity, fix the dates, or run the export again.
  ErrorCode.VALIDATION_RANGE_TOO_LARGE,
  ErrorCode.VALIDATION_INVALID_RANGE,
  ErrorCode.VALIDATION_INVALID_GRANULARITY,
  ErrorCode.BUSINESS_EXPORT_EXPIRED,
  ErrorCode.BUSINESS_EXPORT_NOT_READY,
  ErrorCode.AUTH_INVALID_CREDENTIALS,
  ErrorCode.AUTH_TOKEN_EXPIRED,
  ErrorCode.AUTH_INVALID_TOKEN,
  ErrorCode.AUTH_OTP_INVALID,
  ErrorCode.AUTH_OTP_EXHAUSTED,
  ErrorCode.AUTH_ACCOUNT_LOCKED,
  ErrorCode.RATE_LIMITED,
  ErrorCode.NETWORK_ERROR,
  ErrorCode.CONFLICT,
  // The officer can act on this one: it means "ask an administrator to activate you", which
  // the status pages say in full.
  ErrorCode.PERMISSION_AFFILIATE_INACTIVE,
  // Phase 12. Each has an action the administrator can take right there: say why, re-check the
  // selection, narrow the range, or pick a payment in the right state.
  ErrorCode.VALIDATION_REASON_REQUIRED,
  ErrorCode.VALIDATION_BULK_CONFIRMATION_MISMATCH,
  ErrorCode.VALIDATION_BULK_TOO_LARGE,
  ErrorCode.VALIDATION_AUDIT_RANGE_TOO_LARGE,
  ErrorCode.BUSINESS_SPACE_HAS_TENANCY,
  ErrorCode.BUSINESS_PAYMENT_NOT_REFUNDABLE,
  ErrorCode.BUSINESS_PAYMENT_NOT_VERIFIABLE,
  // Phase 14. Each names the next move: finish the earlier stage, pick a real stage, review
  // the candidate that is already decided, or take the dry-run report to finance.
  ErrorCode.VALIDATION_UNKNOWN_STAGE,
  ErrorCode.BUSINESS_STAGE_OUT_OF_ORDER,
  ErrorCode.BUSINESS_ATTRIBUTION_ALREADY_REVIEWED,
  ErrorCode.BUSINESS_BACKFILL_DECISION_REQUIRED,
  // APMP on WhatsApp (2026-10-12). The owner can act on each from the setup page: upload a real
  // scan, finish the keys step, fix the keys and check again, or try the assistant again once
  // the developer has added its key. The window and opt-out codes never reach a page (the
  // console sends nothing), and the oversize code answers only Meta.
  ErrorCode.VALIDATION_INVALID_DOCUMENT,
  ErrorCode.BUSINESS_WHATSAPP_NOT_CONFIGURED,
  ErrorCode.BUSINESS_WHATSAPP_GRAPH_ERROR,
  ErrorCode.BUSINESS_BOT_UNAVAILABLE,
  // Viewings (2026-10-12). Each is shown where it happened with the next move: ask for another
  // time (full), see the viewing as it now is (cancelled or over), wait for the time to pass
  // before marking attendance.
  ErrorCode.BUSINESS_VIEWING_FULL,
  ErrorCode.BUSINESS_VIEWING_NOT_SCHEDULED,
  ErrorCode.BUSINESS_VIEWING_NOT_PAST,
  // Support officers (2026-10-12). The register form names the existing support officer (restore
  // them instead); resend-welcome says they have already set up; revoke/restore on a row that
  // already moved shows it as it now is.
  ErrorCode.BUSINESS_SUPPORT_OFFICER_EXISTS,
  ErrorCode.BUSINESS_SUPPORT_OFFICER_ALREADY_ACTIVATED,
  ErrorCode.BUSINESS_SUPPORT_OFFICER_INVALID_TRANSITION,
  // Roommates (2026-10-22). The proposal page says the proposal has ended and that APMP keeps
  // looking; the console shows a closed request as it now is.
  ErrorCode.BUSINESS_ROOMMATE_REQUEST_NOT_OPEN,
  ErrorCode.BUSINESS_ROOMMATE_MATCH_CLOSED,
  // Affiliate institutions (2026-10-22). Each is shown where it happened: the campus already has
  // one, the account already belongs to one, the change no longer applies, a password is set.
  ErrorCode.BUSINESS_INSTITUTION_EXISTS,
  ErrorCode.BUSINESS_INSTITUTION_MEMBER_EXISTS,
  ErrorCode.BUSINESS_INSTITUTION_INVALID_TRANSITION,
  ErrorCode.BUSINESS_INSTITUTION_MEMBER_ALREADY_ACTIVATED,
  // Property media (2026-10-27): remove a photo to add another, choose another video; an
  // out-of-order piece is answered by resuming where the server says.
  ErrorCode.BUSINESS_MEDIA_LIMIT_REACHED,
  ErrorCode.VALIDATION_INVALID_VIDEO,
  ErrorCode.CONFLICT_UPLOAD_OFFSET,
  // Accounts on WhatsApp (2026-10-27): sign in instead, add a number, or try again later.
  ErrorCode.BUSINESS_PHONE_ALREADY_REGISTERED,
  ErrorCode.BUSINESS_PHONE_VERIFIED_ELSEWHERE,
  ErrorCode.VALIDATION_NO_WHATSAPP_NUMBER,
  ErrorCode.BUSINESS_WHATSAPP_CODE_NOT_SENT,
  // Confirm a WhatsApp number or email: the confirm step is one tap away.
  ErrorCode.AUTH_CONTACT_UNCONFIRMED,
])
